import React, { useEffect, useState } from "react";

// Pass 56 — returning-customer history, shown at the top of the lead
// drawer (team + admin).
//
// The notification email has carried a one-line "N prior leads" summary
// for a long time, but it threw away the distinction a specialist
// actually needs: a repeat BUYER and a repeat browser who has never
// bought both render as "3 prior leads". This separates them, and names
// the specialist who handled each prior lead.
//
// Naming the colleague is deliberate and is Derek's call: the duty
// person speaks to them directly and they decide between themselves how
// to handle the customer. The system does NOT notify the previous
// specialist — the first touch stays human.
//
// Fetched per-drawer rather than carried on the leads list, because the
// lookup scans the lead book: fine once for an opened drawer, wasteful
// for every row of a 50-row page.

const money = (n) => `MYR ${Math.round(Number(n) || 0).toLocaleString("en-MY")}`;

export default function PriorInteractions({ leadId, apiClient, testIdPrefix = "prior" }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!leadId || !apiClient?.leadHistory) return undefined;
    apiClient.leadHistory(leadId)
      .then((r) => { if (!cancelled) setData(r.data); })
      // Silent: history is helpful context, never load-bearing. A
      // failure here must not block the rep from working the lead.
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [leadId, apiClient]);

  if (!data || !data.count) return null;

  const plural = data.count === 1 ? "enquiry" : "enquiries";
  const bought = data.purchases > 0;

  return (
    <div
      data-testid={`${testIdPrefix}-interactions`}
      style={{
        marginBottom: 18,
        padding: "12px 14px",
        // Green when they have bought before, neutral when they have
        // only ever browsed — the two deserve different handling and
        // the colour carries that before any text is read.
        background: bought ? "#eaf5ea" : "#f4f4f4",
        border: `1px solid ${bought ? "#b9dcb4" : "#e2e2e2"}`,
      }}
    >
      <p style={{ margin: 0, fontSize: 13, color: bought ? "#2f5e3d" : "#5C5C5C", lineHeight: 1.6 }}>
        <strong>Returning customer.</strong>{" "}
        {data.count} prior {plural}
        {data.last_contact ? `, last ${data.last_contact}` : ""}.
        {bought
          ? ` ${data.purchases} purchase${data.purchases === 1 ? "" : "s"}, ${money(data.total_won_value)}.`
          : " No purchase yet."}
      </p>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          background: "transparent", border: "none", padding: "6px 0 0 0",
          fontFamily: "inherit", fontSize: 12, color: "var(--scw-muted)",
          textDecoration: "underline", cursor: "pointer",
        }}
        data-testid={`${testIdPrefix}-interactions-toggle`}
      >
        {expanded ? "Hide history" : "Show history"}
      </button>
      {expanded && (
        <div style={{ marginTop: 10 }}>
          {data.leads.map((l) => (
            <div
              key={l.id}
              style={{
                display: "flex", flexWrap: "wrap", gap: 8,
                padding: "6px 0", borderTop: "1px solid rgba(0,0,0,0.07)",
                fontSize: 12, color: "#5C5C5C",
              }}
              data-testid={`${testIdPrefix}-interactions-row-${l.id}`}
            >
              <span style={{ minWidth: 86 }}>{l.date}</span>
              <span style={{ minWidth: 74 }}>{l.type}</span>
              <span style={{ flex: 1, minWidth: 140 }}>{l.product || "—"}</span>
              <span style={{ minWidth: 92 }}>{l.status}</span>
              <span style={{ minWidth: 78 }}>{l.specialist || "—"}</span>
              {l.won_value > 0 && <span style={{ fontWeight: 500 }}>{money(l.won_value)}</span>}
            </div>
          ))}
          <p style={{ margin: "10px 0 0 0", fontSize: 11, color: "var(--scw-muted)", lineHeight: 1.6 }}>
            Worth a word with the specialist who served them last before you reply.
          </p>
        </div>
      )}
    </div>
  );
}
